<template>
    <v-form ref="metaFieldForm">
        <v-row class="grey lighten-5 mb-2">
            <v-col
            cols="12"
            md="5"
            >
                <v-combobox
                    v-model="meta.key"
                    :items="platformMetaFields"
                    :rules="[v => !!v || 'Meta key is required']"
                    :return-object="false"
                    label="Meta Key"
                    item-text="key"
                    item-value="key"
                    :hint="metaDescription"
                    @input="onKeyInput(meta.key, $event)"
                    persistent-hint
                    required
                >
                    <!-- Custom item template -->
                    <template v-slot:item="{ item, on, attrs }">
                        <v-list-item
                        v-bind="attrs"
                        v-on="on"
                        >
                        <v-list-item-content>
                            <v-list-item-title>{{ item.key }}</v-list-item-title>
                            <v-list-item-subtitle>{{ item.description }}</v-list-item-subtitle>
                        </v-list-item-content>
                        </v-list-item>
                    </template>
                </v-combobox>
            </v-col>

            <v-col
            cols="12"
            md="7"
            >
                <div class="d-flex">
                    <div class="w-100 pr-4">

                        <v-text-field
                            v-if="metaType == 'email'"
                            v-model="meta.value"
                            :rules="[
                                    v => !!v || 'Meta value is required. ' + metaHint,
                                    validateEmails
                                    ]"
                            label="Meta Value"
                            :hint="metaHint"
                            persistent-hint
                            required
                        ></v-text-field>

                        <v-text-field
                            v-else-if="metaType == 'url'"
                            v-model="meta.value"
                            :rules="[
                                    v => !!v || 'Meta value is required. ' + metaHint,
                                    v => /^(https?:\/\/)?([\w-]+(\.[\w-]+)+)(:\d+)?(\/[\w- .\/?%&=]*)?$/.test(v) || 'URL must be valid. ' + metaHint
                                    ]"
                            label="Meta Value"
                            :hint="metaHint"
                            persistent-hint
                            required
                        ></v-text-field>

                        <v-text-field
                            v-else-if="metaType === 'secret'"
                            v-model="meta.value"
                            :append-icon="showSecret ? 'mdi-eye-off' : 'mdi-eye'"
                            :type="showSecret ? 'text' : 'password'"
                            :rules="[v => !!v || 'Meta value is required. ' + metaHint]"
                            label="Meta Value"
                            :hint="metaHint"
                            persistent-hint
                            required
                            @click:append="showSecret = !showSecret"
                        ></v-text-field>

                        <v-text-field
                            v-else-if="metaType == 'integer'"
                            v-model="meta.value"
                            :rules="[
                                    v => !!v || 'Meta value is required. ' + metaHint,
                                    v => /^-?(0|[1-9]\d*)$/.test(v) || 'Value must be an integer. ' + metaHint
                                    ]"
                            label="Meta Value"
                            :hint="metaHint"
                            persistent-hint
                            required
                        ></v-text-field>

                        <v-text-field
                            v-else-if="metaType == 'number'"
                            v-model="meta.value"
                            :rules="[
                                    v => !!v || 'Meta value is required. ' + metaHint,
                                    v => /^-?\d+(\.\d+)?$/.test(v) || 'Value must be a number. ' + metaHint
                                    ]"
                            label="Meta Value"
                            :hint="metaHint"
                            persistent-hint
                            required
                        ></v-text-field>

                        <v-switch
                            class="d-inline-block"
                            v-else-if="metaType == 'switch'"
                            v-model="meta.value"
                            :label="'Switched ' + (meta.value == 'true' ? 'On' : 'Off')"
                            :true-value="'true'"
                            :false-value="'false'"
                            :hint="metaHint"
                            persistent-hint
                        ></v-switch>

                        <div v-else-if="metaType == 'list'">
                            <v-combobox
                            v-model="meta.value"
                            label="Meta Value"
                            multiple
                            chips
                            deletable-chips
                            clearable
                            small-chips
                            delimiter=","
                            :hint="metaHint"
                            persistent-hint
                            :rules="[v => (v && v.length > 0) || 'Meta value is required. ' + metaHint]"
                            :search-input.sync="search"
                            :items="metaOptions"
                            >
                                <template v-slot:no-data>
                                    <v-list-item v-if="search && search.length > 0">
                                        <v-list-item-content>
                                            <v-list-item-title>
                                                Add "<strong>{{ search }}</strong>"?. Press <kbd>enter</kbd> to add to the list
                                            </v-list-item-title>
                                        </v-list-item-content>
                                    </v-list-item>
                                </template>
                            </v-combobox>
                        </div>

                        <div v-else-if="metaType == 'select'">
                            <v-combobox
                            ref="metaSelect"
                            v-model="meta.value"
                            label="Meta Value"
                            :hint="metaHint"
                            persistent-hint
                            clearable
                            :rules="[v => (v && v.length > 0) || 'Meta value is required. ' + metaHint]"
                            :items="metaOptions"
                            />
                        </div>

                        <div v-else-if="metaType == 'json' || metaType == 'object'">
                            <v-text-field
                                class="json-text-field"
                                v-model="meta.value"
                                :rules="[
                                    v => !!v || 'Meta value is required. ' + metaHint,
                                    validateJson
                                    ]"
                                @click:append="expandJson"
                                append-icon="mdi-open-in-new"
                                :hint='metaHint || `Enter valid JSON (e.g. {"freightCost":"10","status":true})`'
                                persistent-hint
                            ></v-text-field>

                            <!-- Expanded JSON Field -->
                            <v-dialog
                            class="json-dialog"
                            v-model="jsonDialog"
                            max-width="900"
                            persistent
                            >
                            <v-card class="no-scrollbar">
                                <v-card-title class="sticky-header d-flex align-center">
                                    <div class="text-h6">{{ meta.key }}</div>
                                    <v-spacer />
                                    <v-btn icon @click="jsonDialog = false">
                                        <v-icon>mdi-close</v-icon>
                                    </v-btn>
                                </v-card-title>



                                <v-card-text class="scrollable-content">
                                    <div>
                                        <v-tabs v-model="expandJsonTab" v-if="metaType == 'object'">
                                            <v-tab>Edit JSON</v-tab>
                                            <v-tab>Schema</v-tab>
                                        </v-tabs>

                                        <v-tabs-items v-model="expandJsonTab">
                                            <v-tab-item>
                                                <div small class="mt-5">{{ metaDescription }}</div>
                                                <v-textarea
                                                    v-model="meta.value"
                                                    @input="() => {this.isValidJson ? JSON.stringify(JSON.parse(this.meta.value)) : this.meta.value}"
                                                    rows="18"
                                                    auto-grow
                                                    hint='Input must be valid JSON format'
                                                    persistent-hint
                                                    class="json-textarea-editor"
                                                />
                                            </v-tab-item>

                                            <v-tab-item>
                                                <div class="json-textarea-editor">
                                                    <div v-if="metaObjectSchema" class="p-4">
                                                        <pre>{{ metaObjectSchema }}</pre>
                                                    </div>
                                                    <span v-else>
                                                        No schema defined.
                                                    </span>
                                                </div>
                                            </v-tab-item>
                                        </v-tabs-items>
                                    </div>

                                </v-card-text>

                                <v-card-actions class="sticky-footer" v-if="expandJsonTab === 0">
                                <span v-if="!isValidJson" class="error--text">Invalid JSON</span>
                                <v-spacer />
                                <v-btn text @click="jsonDialog = false">Cancel</v-btn>
                                <v-btn color="primary" :disabled="!isValidJson" @click="saveJson">Done</v-btn>
                                </v-card-actions>
                            </v-card>
                            </v-dialog>
                        </div>

                        <!-- Default text field for other types -->
                        <v-text-field
                            v-else
                            v-model="meta.value"
                            label="Meta Value"
                            :hint="metaHint"
                            persistent-hint
                            clearable
                            required
                            :rules="[v => !!v || 'Meta value is required. ' + metaHint]"
                        ></v-text-field>

                    </div>
                    <div>
                        <slot name="actions"/>
                    </div>
                </div>

            </v-col>
        </v-row>
    </v-form>
</template>

<script>
import { MetaStruct } from './integrationsMetaStruct';
import { EventBus } from '@/js/event-bus.js'

export class IntegrationMeta {
    constructor({ type, key, value}) {
        this.type = type || null
        this.key = key || null
        this.value = value || null
    }
}

export default {
    props: {
        platformMetaFields: {
            type: Array,
            required: false,
            default: () => [],
        },
        formMeta: {
            type: MetaStruct,
            required: true,
        },
        usedMetaKeys: {
            type: Array,
            required: false,
            default: () => [],
        },
    },
    data() {
        return {
            isFormValid: true,
            meta: this.formMeta,
            beforeValue: JSON.parse(JSON.stringify(this.formMeta.value || '')),
            jsonDialog: false,
            expandJsonTab: 0,
            search: '',
            showSecret: false,
            previousMetaKey: this.formMeta.key || null,
        }
    },

    computed: {

        availableMetaKeys() {
            return this.platformMetaFields.filter(key => !this.usedMetaKeys.includes(key));
        },

        metaDescription() {
            return this.platformMetaFields.find(field => field.key === this.meta.key)?.description || '';
        },

        metaOptions() {
            return this.platformMetaFields.find(field => field.key === this.meta.key)?.options || [];
        },

        metaType() {
            return this.platformMetaFields.find(field => field.key === this.meta.key)?.type || 'string';
        },

        metaObjectSchema() {
            return this.platformMetaFields.find(field => field.key === this.meta.key)?.objectSchema || null;
        },

        metaHint() {
            const existingMeta = this.platformMetaFields.find(field => field.key === this.meta.key) || this.meta;

            const metaType = existingMeta?.type || 'string';
            let hint = "";
            if (metaType == 'list') {
                hint = 'Select or add multiple values';
            } else if (metaType == 'select') {
                hint = 'Select or enter a value';
            } else if (metaType == 'switch') {
                hint = 'Toggle on or off';
            } else if (existingMeta?.hint != "" && existingMeta?.hint != null && typeof existingMeta?.hint !== 'undefined') {
                hint = "Enter value";
            }

            const existingMetaHint = existingMeta?.hint || '';
            if (existingMetaHint.length > 0) {
                hint += ` like "${existingMetaHint}"`;
            }
            return hint;
        },

        isValidJson() {
            try {
                JSON.parse(this.meta.value);
                return true;
            } catch (err) {
                return false;
            }
        },

        isValidObjectSchema() {
            try {
                JSON.parse(this.meta.objectSchema);
                return true;
            } catch (err) {
                return false;
            }
        }
    },

    methods: {

        validate() {
            if (this.$refs.metaSelect) {
                // Flush combobox input into v-model
                this.$refs.metaSelect.blur();
            }
            return this.$refs.metaFieldForm.validate()
        },

        onKeyInput(newKey) {
            if (!newKey) return;

            if (this.usedMetaKeys.includes(newKey)) {
                EventBus.$emit(
                    'show-error',
                    'The ' + newKey + ' key already exists. Please choose a different key.'
                );

                this.$nextTick(() => {
                    this.meta.key = this.previousMetaKey;
                });

                return;
            }

            this.previousMetaKey = newKey;

            const existingMeta = this.platformMetaFields.find(field => field.key === newKey);

            if (existingMeta) {
                // Copy values into current meta (keeps reactivity)
                Object.assign(this.meta, {
                    ...existingMeta,
                    key: newKey, // make sure key stays correct
                });
            }

            if (['list', 'select'].includes(this.meta.type)) {
                this.$nextTick(() => {
                    this.meta.value = '';
                });
            } else if (this.meta.type === 'switch') {
                this.$nextTick(() => {
                    this.meta.value = 'false';
                });
            }
        },

        validateJson(value) {
            if (!value) return true; // allow empty
            return this.isValidJson ? true : 'Invalid JSON format. ';
        },

        validateEmails(value) {
            // let the required rule handle emptiness
            if (!value) return true;

            // accepts a single email or a comma-separated list of emails
            const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
            const allValid = String(value).split(',').every(email => emailRegex.test(email.trim()));

            return allValid || 'Email(s) must be valid. Separate multiple emails with commas. ' + this.metaHint;
        },

        expandJson() {
            this.jsonDialog = true;
            this.meta.value = this.isValidJson ? JSON.stringify(JSON.parse(this.meta.value), null, 2) : this.meta.value;
        },

        saveJson() {
            // remove formatting before saving
            this.meta.value = this.isValidJson ? JSON.stringify(JSON.parse(this.meta.value)) : this.meta.value;
            this.jsonDialog = false;
        },

        removeMeta() {
            this.$emit('removeMeta', this.index);
        }
    }
}
</script>

<style scoped>
.json-textarea ::v-deep(textarea) {
    max-height: 200px;
    /* scrolls if content too tall */
    overflow-y: auto;
    overflow-x: hidden;
    font-family: 'Courier New', monospace;
    /* code-like font */
    font-size: 13px;
    /* smaller font */
    line-height: 1.4;
    /* tighter spacing */
    background-color: #f5f5f5;
    /* optional: light code editor look */
    padding: 8px;
    /* optional padding inside textarea */
}

.json-textarea-editor ::v-deep(textarea) {
    font-family: 'Courier New', monospace;
    /* code-like font */
    font-size: 13px;
    /* smaller font */
    line-height: 1.4;

    background-color: #f5f5f5;
}

.json-dialog {
    overflow-y: hidden;
}

.scrollable-content {
  max-height: 80vh; /* fixed height so scrolling works */
  display: flex;
  flex-direction: column;
  flex: 1;
  overflow-y: auto;
}

/* Sticky header */
.sticky-header {
  position: sticky;
  top: 0;
  z-index: 2;
  background: white;
  border-bottom: 1px solid #e0e0e0;
}

/* Sticky footer */
.sticky-footer {
  position: sticky;
  bottom: 0;
  z-index: 2;
  background: white;
  border-top: 1px solid #e0e0e0;
}

.no-scrollbar {
  overflow-y: hidden;
}
</style>
